import { createApp } from "vue";
import App from "./App.vue";
import { router } from "./router/index.js";
import axios from "axios";
import store from "./store/index.js";
import mitt from "mitt";
import cloneDeep from "lodash/cloneDeep";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { localInsightLightTheme } from "@/themes/LI_light_theme.js";
import { getCookie } from "@/mixins/GetCookie";
import { setCookie } from "@/mixins/SetCookie";

// declare these
const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: "light",
    themes: {
      localInsightLightTheme,
      light: {
        variables: {
          "font-family": "Inter, sans-serif",
        },
      },
    },
  },
});
const app = createApp(App);

// global methods / props
app.config.globalProperties.$cloneDeep = cloneDeep;
app.config.globalProperties.emit = mitt();
app.config.globalProperties.$axios = axios;
app.config.globalProperties.$axios.defaults.withCredentials = true;
app.config.globalProperties.$axios.defaults.baseURL =
  location.protocol + "//" + process.env.VUE_APP_API_PATH;

//allow API call mocking in dev
async function enableMocking() {
  if (!["dev", "stg"].includes(process.env.VUE_APP_ENVIRONMENT)) {
    return; //bypass mocking
  }

  const { mockingWorker } = await import("./mocks/browser");

  // resolves promise once the Mocking Service Worker is up and ready to intercept requests.
  return mockingWorker.start({
    //routes not defined in mock handlers to default to real backend calls
    onUnhandledRequest() {
      return;
    },
  });
}

// add these to the app
app.use(router);
app.use(store);
app.use(vuetify);

// axios default headers
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

// Add axios interceptor for token refresh
let lastTokenReset = 0;
const DEBOUNCE_TIME = 2 * 60 * 1000; // 2 minutes in milliseconds
let sessionExpiryCountdownTimer = null;

// Store the interceptor function for testing
const sessionExpiryInterceptor = (response) => {
  const token = getCookie("BEARER_TOKEN");
  if (token) {
    const now = Date.now();

    // Only reset if it's been more than 2 minutes since last reset
    if (now - lastTokenReset > DEBOUNCE_TIME) {
      setCookie("BEARER_TOKEN", token, 2);
      lastTokenReset = now;
      clearTimeout(sessionExpiryCountdownTimer);
      // start the expiry countdown timer
      sessionExpiryCountdownTimer = setTimeout(
        function () {
          // Emit event to App.vue to show session expiry warning
          app.config.globalProperties.emit.emit("session-expiring");
        },
        1 * 60 * 60 * 1000 + 55 * 60 * 1000,
      ); // 1 hour and 55 minutes in milliseconds
    }
  }
  return response;
};

// Add the interceptor
axios.interceptors.response.use(sessionExpiryInterceptor, (error) => {
  return Promise.reject(error);
});

// Test function to manually trigger the interceptor
window.testSessionExpiry = () => {
  const mockResponse = { data: {} };
  sessionExpiryInterceptor(mockResponse);
};

// Log the user out if they try to access an API they don't have access to.
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    //return enforced timeout/cancelled requests to caller
    if (
      Object.prototype.hasOwnProperty.call(error, "code") &&
      error.code === "ECONNABORTED"
    ) {
      return Promise.reject(error);
    }

    //return script initiated abortController cancellation request to caller
    if (error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    //return non-exception fatal errors from frontend e.g. max_execution_time for graceful frontend response
    if (error.code === "ERR_NETWORK") {
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      store.commit("resetConfig");
      axios
        .post("/logout")
        .catch(function (error) {
          // it would appear there is something wrong...probably an invalid bearer token
          console.error("(logout) invalid user details and or token" + error);
        })
        .then(function () {
          // clear the bearer cookie and reload the app
          document.cookie =
            "BEARER_TOKEN=; domain=.localinsight.org; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
          router.go();
        });
    } else {
      return Promise.reject(error);
    }
  },
);

// open the portal!
enableMocking().then(() => {
  app.mount("#app");
});
